// src/components/checkout/OrderSummary.js

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../../context/ProductContext';
import Button from '../common/Button';
import { fetchData } from '../../utils/api';

function OrderSummary({
  cart,
  total: subtotal,
  paymentConfirmed = false,
  shippingAddress,
  readOnly = false,
}) {
  const { products } = useProducts();

  const [business, setBusiness] = useState({
    name: '',
    abn: '',
  });

  const [vatRate, setVatRate] = useState(0);
  const [shippingFee, setShippingFee] = useState(null);
  const [error, setError] = useState('');
  const [validCart, setValidCart] = useState([]);

  const money = (value) =>
    new Intl.NumberFormat('en-SL', {
      style: 'currency',
      currency: 'SLE',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      try {
        const data = await fetchData('/website/business-details', {
          noCache: true,
        });

        setBusiness(
          data?.business || {
            name: 'Business',
            abn: 'Unknown',
          },
        );

        setVatRate(Number(data?.fees?.vat?.vatRate || 0));
        setShippingFee(Number(data?.fees?.shipping?.shippingFee || 0));
        setError('');
      } catch (err) {
        setError(
          'Failed to load business details or fees. Using default values.',
        );

        setBusiness({
          name: 'Business',
          abn: 'Unknown',
        });

        setVatRate(0);
        setShippingFee(0);
      }
    };

    fetchBusinessDetails();
  }, []);

  useEffect(() => {
    const validated = (cart || [])
      .map((item) => {
        const isMerchantItem = Boolean(
          item.merchant_id &&
            item.source_product_id !== undefined,
        );

        const product = isMerchantItem
          ? item
          : products.find(
              (candidate) =>
                String(candidate.id) === String(item.id),
            );

        if (
          !product ||
          (!isMerchantItem &&
            ['real_estate', 'cars'].includes(product.category))
        ) {
          return null;
        }

        return {
          ...item,
          product,
        };
      })
      .filter(Boolean);

    setValidCart(validated);
  }, [cart, products]);

  const safeSubtotal = Number(subtotal || 0);
  const safeVatRate = Number(vatRate || 0);
  const shipping = Number(shippingFee || 0);

  const vat = safeSubtotal * (safeVatRate / 100);
  const grandTotal = safeSubtotal + vat + shipping;

  const getImageSrc = (item) => {
    if (item.cart_image) {
      return item.cart_image;
    }

    const product = item.product;
    const baseUrl = process.env.REACT_APP_PRODUCT_URL || '';
    const productImage = product?.image || '';

    if (!productImage) {
      return '/assets/images/placeholder.jpg';
    }

    return `${baseUrl}${productImage}`;
  };

  return (
    <div className="rounded-lg border border-primary/10 bg-cream p-6 shadow-md">
      <h2 className="mb-2 font-playfair-display text-xl font-semibold text-primary">
        Order Summary
      </h2>

      <p className="mb-4 font-montserrat text-sm text-primary/70">
        {business.name}

        {business.abn && business.abn !== 'Unknown'
          ? ` (ABN: ${business.abn})`
          : ''}
      </p>

      {error && (
        <p className="mb-4 font-montserrat text-sm text-red-600">
          {error}
        </p>
      )}

      {validCart.length > 0 ? (
        <div
          className="space-y-4"
          role="list"
          aria-label="Cart items in order summary"
        >
          {validCart.map((item) => {
            const quantity = Number(item.quantity || 0);
            const unitPrice = Number(item.price || 0);
            const lineTotal = quantity * unitPrice;

            return (
              <div
                key={
                  item.variantKey ||
                  `${item.id}-${item.selectedColor || ''}-${
                    item.selectedSize || ''
                  }`
                }
                className="flex items-center gap-4 rounded-md bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
                role="listitem"
              >
                <img
                  src={getImageSrc(item)}
                  alt={`${item.name} in order summary`}
                  className="h-16 w-16 rounded object-cover"
                  onError={(event) => {
                    event.currentTarget.src =
                      '/assets/images/placeholder.jpg';
                  }}
                />

                <div className="min-w-0 flex-1">
                  <p className="font-montserrat font-medium text-primary">
                    {item.name}
                  </p>

                  <p className="font-montserrat text-sm text-primary/70">
                    Qty: {quantity}

                    {item.selectedSize &&
                      ` • Size: ${item.selectedSize}`}

                    {item.selectedColor &&
                      ` • Color: ${item.selectedColor}`}

                    {item.format &&
                      ` • Format: ${item.format}`}
                  </p>

                  <p className="mt-1 font-montserrat text-xs text-primary/60">
                    {money(unitPrice)} each
                  </p>
                </div>

                <p className="shrink-0 font-montserrat font-medium text-primary">
                  {money(lineTotal)}
                </p>
              </div>
            );
          })}

          <div className="mt-4 border-t border-primary/20 pt-4">
            <div className="mb-2 flex justify-between font-montserrat text-secondary">
              <span>Subtotal</span>
              <span>{money(safeSubtotal)}</span>
            </div>

            <div className="mb-2 flex justify-between font-montserrat text-secondary">
              <span>VAT ({safeVatRate}%)</span>
              <span>{money(vat)}</span>
            </div>

            <div className="mb-4 flex justify-between font-montserrat text-secondary">
              <span>Shipping</span>

              <span>
                {shippingFee === null
                  ? 'Calculated at next step'
                  : money(shipping)}
              </span>
            </div>

            <div className="flex justify-between font-montserrat text-lg font-semibold text-primary">
              <span>Total</span>
              <span>{money(grandTotal)}</span>
            </div>
          </div>

          {paymentConfirmed && shippingAddress && (
            <div className="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center">
              <p className="mb-2 text-sm font-medium text-yellow-800">
                Receipt will be available after successful payment.
              </p>

              <Button
                disabled
                className="cursor-not-allowed bg-gray-300 px-6 py-3 text-gray-600"
              >
                Download Receipt (After Payment)
              </Button>
            </div>
          )}

          {!readOnly && (
            <Link
              to="/cart"
              className="block w-full py-2 text-center font-montserrat text-primary hover:underline"
              aria-label="Edit cart from order summary"
            >
              Edit Cart
            </Link>
          )}
        </div>
      ) : (
        <p
          className="font-montserrat text-primary"
          role="alert"
        >
          No valid items in your order.
        </p>
      )}
    </div>
  );
}

export default OrderSummary;